import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { fetchCycles } from '../services/reviewCycleService';
import { fetchFormsForCycle } from '../services/reviewFormService';
import { exportToExcel, exportToPDF } from '../services/exportService';
import ScoreBar from '../components/ScoreBar';
import WorkflowStatusBadge from '../components/WorkflowStatusBadge';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, Radar, Cell,
} from 'recharts';
import type { ReviewForm, ReviewCycle } from '../types';
import { WORKFLOW_STEPS } from '../types';

const EPRReports: React.FC = () => {
  const { cycleId: paramCycleId } = useParams<{ cycleId?: string }>();
  useAuth();
  const [cycles, setCycles] = useState<ReviewCycle[]>([]);
  const [selectedCycleId, setSelectedCycleId] = useState(paramCycleId ?? '');
  const [cycle, setCycle] = useState<ReviewCycle | null>(null);
  const [forms, setForms] = useState<ReviewForm[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'score' | 'status'>('name');

  useEffect(() => {
    fetchCycles().then(c => {
      setCycles(c);
      if (!paramCycleId && c.length > 0) {
        const active = c.find(x => x.status === 'active') ?? c[0];
        setSelectedCycleId(active.id);
      }
    });
  }, [paramCycleId]);

  useEffect(() => {
    if (!selectedCycleId) return;
    loadData(selectedCycleId);
  }, [selectedCycleId]);

  const loadData = async (cid: string) => {
    setLoading(true);
    try {
      const [f, allCycles] = await Promise.all([fetchFormsForCycle(cid), fetchCycles()]);
      setForms(f);
      setCycle(allCycles.find(c => c.id === cid) ?? null);
    } catch (e) {
      console.error('Failed to load report data', e);
    } finally {
      setLoading(false);
    }
  };

  // ─── Derived stats ─────────────────────────────────────────────────────────

  const completedForms = forms.filter(f => ['approved', 'signed_off'].includes(f.status));
  const formsWithScore = forms.filter(f => f.computedScore != null);

  const avgScore = formsWithScore.length
    ? formsWithScore.reduce((s, f) => s + (f.computedScore ?? 0), 0) / formsWithScore.length
    : 0;

  const completionPct = forms.length ? Math.round((completedForms.length / forms.length) * 100) : 0;

  // Status breakdown for pie/bar
  const statusData = WORKFLOW_STEPS.map(s => ({
    name: s.replace('_', ' '),
    count: forms.filter(f => f.status === s).length,
  })).filter(d => d.count > 0);

  // Score distribution
  const BUCKETS = ['1.0–1.9', '2.0–2.9', '3.0–3.4', '3.5–3.9', '4.0–4.4', '4.5–5.0'];
  const RANGES = [[1.0, 1.9], [2.0, 2.9], [3.0, 3.4], [3.5, 3.9], [4.0, 4.4], [4.5, 5.0]];
  const scoreDistData = BUCKETS.map((label, i) => ({
    range: label,
    count: formsWithScore.filter(f => {
      const s = f.finalScore ?? f.calibratedScore ?? f.computedScore ?? 0;
      return s >= RANGES[i][0] && s <= RANGES[i][1];
    }).length,
  }));

  // OPTICS spider for all forms (average per criteria)
  const allOpticsKeys = Array.from(
    new Set(forms.flatMap(f => Object.keys(f.managerReview?.opticsScores ?? f.selfReview?.opticsScores ?? {}))),
  );
  const radarData = allOpticsKeys.map(k => {
    const vals = forms
      .map(f => f.managerReview?.opticsScores?.[k] ?? f.selfReview?.opticsScores?.[k])
      .filter((v): v is number => v != null && v > 0);
    return { subject: k, avg: vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0 };
  });

  // Filtered + sorted forms
  const filteredForms = forms
    .filter(f => f.revieweeName.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'score') return (b.finalScore ?? b.computedScore ?? 0) - (a.finalScore ?? a.computedScore ?? 0);
      if (sortBy === 'status') return WORKFLOW_STEPS.indexOf(b.status) - WORKFLOW_STEPS.indexOf(a.status);
      return a.revieweeName.localeCompare(b.revieweeName);
    });

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-900">EPR Reports</h1>
        {cycle && forms.length > 0 && (
          <div className="flex gap-2">
            <button
              onClick={async () => { try { await exportToExcel(forms, cycle!); } catch (e) { alert('Export failed: ' + String(e)); } }}
              className="text-xs bg-brand-success/20 border border-brand-success/40 text-emerald-600 px-3 py-1.5 rounded-lg hover:bg-brand-success/30 transition"
            >
              📥 Export Excel
            </button>
            <button
              onClick={async () => { try { await exportToPDF(forms, cycle!); } catch (e) { alert('Export failed: ' + String(e)); } }}
              className="text-xs bg-red-50 border border-red-200 text-red-600 px-3 py-1.5 rounded-lg hover:bg-brand-error/30 transition"
            >
              📄 Export PDF
            </button>
          </div>
        )}
      </div>

      {/* Cycle selector */}
      <select
        value={selectedCycleId}
        onChange={e => setSelectedCycleId(e.target.value)}
        className="bg-white text-gray-900 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-brand-accent focus:outline-none"
      >
        <option value="">— select cycle —</option>
        {cycles.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>

      {loading && <p className="text-gray-500 animate-pulse">Loading…</p>}

      {!loading && selectedCycleId && (
        <>
          {/* KPI summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Total Forms', value: forms.length, color: 'text-gray-900' },
              { label: 'Completed', value: completedForms.length, color: 'text-emerald-600' },
              { label: 'Completion', value: `${completionPct}%`, color: 'text-blue-600' },
              { label: 'Avg Score', value: avgScore > 0 ? avgScore.toFixed(2) : '—', color: 'text-brand-warning' },
            ].map(card => (
              <div key={card.label} className="bg-white border border-gray-200 shadow-sm rounded-xl p-4 text-center">
                <p className={`text-3xl font-bold ${card.color}`}>{card.value}</p>
                <p className="text-xs text-gray-500 mt-1">{card.label}</p>
              </div>
            ))}
          </div>

          {/* Charts row */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Score distribution */}
            <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-5">
              <h2 className="text-sm font-semibold text-gray-700 mb-3">Score Distribution</h2>
              {scoreDistData.some(d => d.count > 0) ? (
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={scoreDistData} margin={{ left: -20 }}>
                    <XAxis dataKey="range" tick={{ fill: '#6b7280', fontSize: 10 }} />
                    <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6 }}
                      labelStyle={{ color: '#e2e8f0' }}
                      itemStyle={{ color: '#94a3b8' }}
                    />
                    <Bar dataKey="count" radius={[4,4,0,0]} fill="#3b82f6">
                      {scoreDistData.map((_, i) => <Cell key={i} fill={['#ef4444','#f59e0b','#3b82f6','#10b981','#10b981','#10b981'][i]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-gray-400 text-xs py-8 text-center">No scores yet.</p>}
            </div>

            {/* OPTICS radar */}
            <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-5">
              <h2 className="text-sm font-semibold text-gray-700 mb-3">Avg OPTICS Scores</h2>
              {radarData.length > 0 ? (
                <ResponsiveContainer width="100%" height={160}>
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#334155" />
                    <PolarAngleAxis dataKey="subject" tick={{ fill: '#6b7280', fontSize: 10 }} />
                    <Radar dataKey="avg" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} />
                    <Tooltip
                      contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6 }}
                      formatter={(v: number) => v.toFixed(2)}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              ) : <p className="text-gray-400 text-xs py-8 text-center">No OPTICS data yet.</p>}
            </div>
          </div>

          {/* Workflow status bar */}
          <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-5">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Workflow Status Breakdown</h2>
            <ResponsiveContainer width="100%" height={100}>
              <BarChart data={statusData} layout="vertical" margin={{ left: 60, right: 10 }}>
                <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 10 }} allowDecimals={false} />
                <YAxis dataKey="name" type="category" tick={{ fill: '#6b7280', fontSize: 10 }} width={100} />
                <Tooltip
                  contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6 }}
                  labelStyle={{ color: '#e2e8f0' }}
                />
                <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Forms table */}
          <div className="bg-white border border-gray-200 shadow-sm rounded-xl overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center gap-3 flex-wrap">
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name…"
                className="bg-white text-gray-900 text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:border-brand-accent focus:outline-none"
              />
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value as typeof sortBy)}
                className="bg-white text-gray-900 text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:border-brand-accent focus:outline-none"
              >
                <option value="name">Sort: Name</option>
                <option value="score">Sort: Score (high→low)</option>
                <option value="status">Sort: Status</option>
              </select>
              <span className="text-xs text-gray-400 ml-auto">{filteredForms.length} results</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 border-b border-gray-200">
                    <th className="text-left p-3">Employee</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-center p-3">Computed</th>
                    <th className="text-center p-3">Calibrated</th>
                    <th className="text-center p-3">Adj</th>
                    <th className="text-center p-3">Final</th>
                    <th className="text-left p-3 w-32">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredForms.map(f => (
                    <tr key={f.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="p-3">
                        <p className="font-medium text-gray-900 text-sm">{f.revieweeName}</p>
                        <p className="text-xs text-gray-400">Reviewer: {f.reviewerName || f.reviewerId}</p>
                      </td>
                      <td className="p-3">
                        <WorkflowStatusBadge status={f.status} size="sm" />
                      </td>
                      <td className="p-3 text-center text-gray-700 text-sm">
                        {f.computedScore?.toFixed(2) ?? '—'}
                      </td>
                      <td className="p-3 text-center text-gray-700 text-sm">
                        {f.calibratedScore?.toFixed(2) ?? '—'}
                      </td>
                      <td className={`p-3 text-center text-sm font-medium ${
                        f.discretionAdjustment != null
                          ? f.discretionAdjustment >= 0 ? 'text-emerald-600' : 'text-red-600'
                          : 'text-gray-400'
                      }`}>
                        {f.discretionAdjustment != null
                          ? (f.discretionAdjustment >= 0 ? '+' : '') + f.discretionAdjustment.toFixed(2)
                          : '—'}
                      </td>
                      <td className="p-3 text-center">
                        <span className={`font-bold text-sm ${f.finalScore != null ? 'text-emerald-600' : 'text-gray-400'}`}>
                          {f.finalScore?.toFixed(2) ?? '—'}
                        </span>
                      </td>
                      <td className="p-3">
                        {(f.finalScore ?? f.computedScore) != null && (
                          <ScoreBar score={f.finalScore ?? f.computedScore!} showValue={false} size="sm" />
                        )}
                      </td>
                    </tr>
                  ))}
                  {filteredForms.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-gray-400 text-sm">No forms found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EPRReports;
